import { PillLink } from "./PillLink";
import { entrepreneurPoints, mentorPoints, routes, sectionShell } from "./landingContent";

interface AudienceCardProps {
  label: string;
  headline: string;
  points: string[];
  cta: { to: string; text: string };
  /** Colour treatment for the card surface and its type. */
  tone: "green" | "blue";
}

function AudienceCard({ label, headline, points, cta, tone }: AudienceCardProps) {
  const isGreen = tone === "green";

  return (
    <div
      className={`rounded-[28px] px-8 py-10 lg:px-12 lg:py-[52px] ${
        isGreen ? "bg-eab-green text-eab-ink" : "bg-eab-blue-dark text-white"
      }`}
    >
      <p
        className={`mb-[18px] text-[16px] font-semibold ${isGreen ? "" : "text-eab-green"}`}
      >
        {label}
      </p>
      <p className="mb-6 max-w-[420px] text-pretty text-[28px] font-bold leading-[1.12] tracking-[-0.025em] lg:text-[34px]">
        {headline}
      </p>
      <ul className={`mb-[30px] flex flex-col gap-2.5 ${isGreen ? "" : "text-white/90"}`}>
        {points.map((point) => (
          <li key={point} className="flex items-start gap-3 text-[17px] font-medium">
            <span
              className={isGreen ? "text-eab-ink/50" : "text-white/50"}
              aria-hidden="true"
            >
              •
            </span>
            <span className="text-pretty">{point}</span>
          </li>
        ))}
      </ul>
      <PillLink to={cta.to} variant={isGreen ? "ink" : "white"}>
        {cta.text}
      </PillLink>
    </div>
  );
}

/** Two equal cards splitting the audience: entrepreneurs (green), mentors (blue). */
export default function AudienceSplit() {
  return (
    <section className={`${sectionShell} pt-[76px]`}>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <AudienceCard
          tone="green"
          label="For entrepreneurs"
          headline="Get focused guidance from mentors who've solved the challenges you're facing."
          points={entrepreneurPoints}
          cta={{ to: routes.signUpEntrepreneur, text: "Join as an entrepreneur" }}
        />
        <AudienceCard
          tone="blue"
          label="For mentors"
          headline="Share your expertise and help shape the next generation of entrepreneurs."
          points={mentorPoints}
          cta={{ to: routes.signUpMentor, text: "Join as a mentor" }}
        />
      </div>
    </section>
  );
}
